/* global google */

/**
 * Loads the Google Maps JS API once and shares the promise between instances.
 * @param {string} apiKey - Google Maps API key.
 * @returns {Promise}
 */
function storeFinderLoadMaps(apiKey) {
  if (storeFinderLoadMaps.promise) return storeFinderLoadMaps.promise;

  storeFinderLoadMaps.promise = new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    window.initStoreFinderMaps = resolve;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=initStoreFinderMaps`;
    script.async = true;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return storeFinderLoadMaps.promise;
}

if (!customElements.get('store-finder')) {
  const StoreFinder = class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.storeFinderInit) return;
      this.dataset.storeFinderInit = 'true';

      const dataEl = this.querySelector('[data-store-finder-data]');
      if (!dataEl) return; // no locations, nothing to wire up

      this.stores = JSON.parse(dataEl.textContent).map((store) => ({
        ...store,
        lat: store.lat !== null ? parseFloat(store.lat) : null,
        lng: store.lng !== null ? parseFloat(store.lng) : null,
      }));

      this.apiKey = this.dataset.googleMapsApiKey;
      this.mapEl = this.querySelector('[data-store-finder-map]');
      this.listEl = this.querySelector('[data-store-finder-list]');
      this.form = this.querySelector('[data-store-finder-form]');
      this.input = this.querySelector('[data-store-finder-input]');
      this.searchBtn = this.querySelector('[data-store-finder-search]');
      this.locateBtn = this.querySelector('[data-store-finder-locate]');
      this.radiusSelect = this.querySelector('[data-store-finder-radius]');
      this.emptyEl = this.querySelector('[data-store-finder-empty]');
      this.statusEl = this.querySelector('[data-store-finder-status]');
      this.locateDefaultHtml = this.locateBtn.innerHTML;
      this.locateDefaultLabel = this.locateBtn.getAttribute('aria-label');

      this.map = null;
      this.markers = []; // { handle, marker }
      this.userMarker = null;
      this.lastLocation = null; // { lat, lng } of the most recent search/geolocation

      // Map each server-rendered card to its store handle, so filtering can
      // reorder/hide the existing DOM instead of rebuilding card markup.
      this.cards = new Map();
      this.listEl.querySelectorAll('[data-handle]').forEach((el) => {
        this.cards.set(el.dataset.handle, el);
      });

      if (this.radiusSelect) {
        this.radiusSelect.addEventListener('change', () => {
          if (this.lastLocation) this.applyRadiusFilter();
        });
      }

      this.locateBtn.addEventListener('click', () => this.locate());

      if (!this.apiKey) {
        // No key configured (list-only mode): address search can't work, so hide
        // its dead controls instead of leaving them permanently disabled.
        this.hideAddressSearch();
        return;
      }

      window.initLazyScript(this, this.initMap.bind(this));
    }

    hideAddressSearch() {
      if (this.input) this.input.hidden = true;
      if (this.searchBtn) this.searchBtn.hidden = true;
    }

    haversineMiles(lat1, lng1, lat2, lng2) {
      const R = 3958.8; // Earth radius, miles
      const toRad = (d) => (d * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    computeDistancesFrom(lat, lng) {
      const distances = new Map();
      const located = this.stores.filter((s) => s.lat !== null && s.lng !== null);
      located.forEach((s) => distances.set(s.handle, this.haversineMiles(lat, lng, s.lat, s.lng)));
      const sorted = [...located].sort((a, b) => distances.get(a.handle) - distances.get(b.handle));
      return { sorted, distances };
    }

    currentRadius() {
      return this.radiusSelect ? parseFloat(this.radiusSelect.value) : Infinity;
    }

    setDistance(card, distance) {
      let span = card.querySelector('.store-finder__distance');

      if (distance === null || distance === undefined) {
        if (span) span.remove();
        return;
      }

      if (!span) {
        span = document.createElement('span');
        span.className = 'store-finder__distance';
        card.appendChild(span);
      }

      span.textContent = this.dataset.distanceText.replace('[distance]', distance.toFixed(1));
    }

    // Re-filters the list (and map markers) for the last resolved location using
    // whatever radius is currently selected. Stores without coordinates can't be
    // placed relative to a radius, so they're excluded once a location-based
    // search is active (unlike the unfiltered initial list, which shows
    // everything). The server-rendered cards are hidden/reordered in place so
    // the card markup only lives in snippets/store-card.liquid.
    applyRadiusFilter() {
      if (!this.lastLocation) return [];
      const { lat, lng } = this.lastLocation;
      const { sorted, distances } = this.computeDistancesFrom(lat, lng);
      const radius = this.currentRadius();
      const withinRadius = sorted.filter((s) => distances.get(s.handle) <= radius);
      const withinHandles = new Set(withinRadius.map((s) => s.handle));

      this.cards.forEach((card, handle) => {
        card.hidden = !withinHandles.has(handle);
      });

      withinRadius.forEach((s) => {
        const card = this.cards.get(s.handle);
        if (!card) return;
        this.listEl.appendChild(card);
        this.setDistance(card, distances.get(s.handle));
      });

      if (this.map) {
        this.markers.forEach(({ handle, marker }) => {
          marker.setMap(withinHandles.has(handle) ? this.map : null);
        });
      }

      if (this.emptyEl) {
        if (withinRadius.length) {
          this.emptyEl.hidden = true;
        } else {
          this.emptyEl.hidden = false;
          this.emptyEl.textContent = this.dataset.noResultsText.replace('[distance]', radius);
        }
      }

      return withinRadius;
    }

    placeUserMarker(lat, lng) {
      if (!this.map) return;
      if (this.userMarker) this.userMarker.setMap(null);
      this.userMarker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#1a73e8',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
        title: this.dataset.yourLocationText,
      });
    }

    fitMapToStores(storeList, lat, lng) {
      if (!this.map) return;
      const bounds = new google.maps.LatLngBounds();
      if (lat !== undefined && lng !== undefined) bounds.extend({ lat, lng });
      storeList.slice(0, 5).forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      if (!bounds.isEmpty()) this.map.fitBounds(bounds, 60);
    }

    handleResolvedLocation(lat, lng) {
      this.lastLocation = { lat, lng };
      const withinRadius = this.applyRadiusFilter();
      this.placeUserMarker(lat, lng);
      this.fitMapToStores(withinRadius, lat, lng);
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    setLocateState(symbol, label) {
      this.locateBtn.innerHTML = symbol;
      this.locateBtn.setAttribute('aria-label', label);
    }

    restoreLocateButton() {
      this.locateBtn.innerHTML = this.locateDefaultHtml;
      this.locateBtn.setAttribute('aria-label', this.locateDefaultLabel);
      this.locateBtn.disabled = false;
      this.locateBtn.removeAttribute('aria-busy');
    }

    locate() {
      if (!navigator.geolocation) {
        this.setStatus(this.dataset.geolocationUnsupportedText);
        return;
      }

      clearTimeout(this.locateResetTimer);
      this.setStatus('');
      this.locateBtn.disabled = true;
      this.locateBtn.setAttribute('aria-busy', 'true');
      this.setLocateState('…', this.dataset.locatingText);

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          this.handleResolvedLocation(coords.latitude, coords.longitude);
          this.setLocateState('✓', this.dataset.locationFoundText);
          this.locateResetTimer = setTimeout(() => this.restoreLocateButton(), 2000);
        },
        () => {
          this.setStatus(this.dataset.geolocationErrorText);
          this.setLocateState('⚠', this.dataset.locationErrorText);
          this.locateResetTimer = setTimeout(() => this.restoreLocateButton(), 2000);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
      );
    }

    // Build the info window body from DOM nodes so merchant-authored metaobject
    // values are inserted as text and never parsed as HTML.
    buildInfoContent(store) {
      const content = document.createElement('div');
      content.className = 'store-finder__infowindow';

      const name = document.createElement('strong');
      name.textContent = store.name;

      const address = document.createElement('span');
      address.textContent = store.address1;

      const locality = document.createElement('span');
      locality.textContent = `${store.city}, ${store.state} ${store.zip}`;

      content.append(name, address, locality);
      return content;
    }

    initMap() {
      storeFinderLoadMaps(this.apiKey).then(() => {
        this.map = new google.maps.Map(this.mapEl, {
          center: { lat: 39.5, lng: -98.35 }, // continental US
          zoom: 4,
        });

        this.markers = this.stores
          .filter((s) => s.lat !== null && s.lng !== null)
          .map((store) => {
            const marker = new google.maps.Marker({
              position: { lat: store.lat, lng: store.lng },
              map: this.map,
              title: store.name,
            });
            const infoWindow = new google.maps.InfoWindow({
              content: this.buildInfoContent(store),
            });
            marker.addListener('click', () => infoWindow.open(this.map, marker));
            return { handle: store.handle, marker };
          });

        if (this.markers.length) {
          const bounds = new google.maps.LatLngBounds();
          this.markers.forEach(({ marker }) => bounds.extend(marker.getPosition()));
          this.map.fitBounds(bounds, 40);
        }

        this.input.removeAttribute('disabled');
        this.searchBtn.removeAttribute('disabled');

        const geocoder = new google.maps.Geocoder();
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          const address = this.input.value.trim();
          if (!address) return;

          this.setStatus('');
          this.searchBtn.disabled = true;
          geocoder.geocode({ address }, (results, status) => {
            this.searchBtn.disabled = false;
            if (status !== 'OK' || !results[0]) {
              this.setStatus(this.dataset.geocodeErrorText);
              return;
            }
            const { location } = results[0].geometry;
            this.handleResolvedLocation(location.lat(), location.lng());
          });
        });
      }).catch(() => {
        // The Maps script failed to load (offline, blocked, or API error): the
        // map and address search can't work, so remove their dead elements and
        // explain. Geolocation sorting and the list still work.
        if (this.mapEl) this.mapEl.remove();
        this.hideAddressSearch();
        this.setStatus(this.dataset.searchUnavailableText);
      });
    }
  };

  customElements.define('store-finder', StoreFinder);
}
